import { defineStore } from 'pinia'
import { ref, computed, triggerRef } from 'vue'
import type { OAuthCredential, User } from 'firebase/auth'
import {
  loginWithEmail,
  linkGoogleCredential,
  loginWithGoogle as authLoginWithGoogle,
  registerWithEmail as authRegisterWithEmail,
  sendEmailVerification,
  logout as firebaseLogout,
} from '@/services/firebase/auth.service'
import { userService } from '@/services/userService'
import { provisionProfessorAccount } from '@/services/accountProvisioning'
import { deriveDisplayName } from '@/utils/displayName'
import { fetchUserLegalState } from '@/services/legalConsentService'
import { useLegalConsentStore } from '@/stores/legalConsent'

export interface GoogleLoginOptions {
  /**
   * `true` sólo desde el REGISTRO, donde la casilla de términos ya se marcó: si la
   * cuenta es nueva se aprovisiona y se registra la aceptación (`signup_checkbox`).
   * Desde el login es `false`: una cuenta nueva NO se aprovisiona aquí — el guard
   * de consentimiento abre «Un último paso» y aprovisiona al «Continuar» (ADR-SEC-018).
   */
  provisionIfNew?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const userDocument = ref<any>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isRegistering = ref(false) // Flag para indicar que estamos en proceso de registro

  const isAuthenticated = computed(() => user.value !== null)
  const userId = computed(() => user.value?.uid ?? null)
  const userEmail = computed(() => user.value?.email ?? null)
  const displayName = computed(() => user.value?.displayName ?? null)
  // (RETIRADOS 2026-10-08, ADR-SEC-020) `userRole` / `isProfessor` — derivaban «es
  // profesor» de `users/{uid}.role`, que sólo dice en qué app se creó la cuenta. El
  // profesor es quien tiene `professor_profiles/{uid}` (lo asegura
  // `EnsureProfessorProfileGuard`). Su único consumidor era el `RequiresRoleGuard`
  // inerte, retirado con ellos. Ver specs/CAMBIOS-HISTORICOS/20261008-profesor-por-perfil.md
  // (RETIRADO 2026-09-07) `isAdmin` — derivaba el rol del DOCUMENTO `users/{uid}`,
  // que desde ADR-SEC-016 es un espejo y no autoridad; el rol `admin` se opera desde
  // `quizoteca-backoffice` y no tiene SPA en este repo (cierre de DEUDA-INICIAL §34).
  // No tenía un solo consumidor. Ver
  // specs/CAMBIOS-HISTORICOS/20260907-rol-admin-solo-claim-minimo-privilegio.md

  function setUser(newUser: User | null, skipLoadDocument = false) {
    user.value = newUser

    // Si estamos en proceso de registro, NO cargar el documento aquí
    // El proceso de registro lo manejará manualmente
    if (isRegistering.value) {
      console.log('🔄 Registro en progreso, omitiendo carga automática de documento')
      return
    }

    // Si hay usuario, cargar documento de Firestore (a menos que se solicite omitir)
    if (newUser && !skipLoadDocument) {
      loadUserDocument(true) // Silent mode para evitar warnings durante el flujo normal
    } else if (!newUser) {
      userDocument.value = null
    }
  }

  async function loadUserDocument(silent = false) {
    try {
      if (!user.value?.uid) return

      // Usar userService en lugar de acceso directo a Firestore
      const doc = await userService.getUserDocument(user.value.uid)

      if (doc) {
        userDocument.value = doc
        if (!silent) {
          console.log('✅ Documento de usuario cargado exitosamente')
        }
      } else {
        if (!silent) {
          console.warn('⚠️  Documento de usuario no encontrado en Firestore')
        }
        userDocument.value = null
      }
    } catch (err) {
      if (!silent) {
        console.error('❌ Error al cargar documento de usuario:', err)
      }
      userDocument.value = null
    }
  }

  /**
   * Fuerza a re-evaluar todo lo que lee `user` (cabecera, saludo). Hace falta
   * después de escribir el nombre en Firebase Auth: `updateProfile` muta en sitio
   * el mismo objeto `User` y Vue no lo ve (ADR-023).
   */
  function refreshUser() {
    triggerRef(user)
  }

  function setLoading(value: boolean) {
    loading.value = value
  }

  function setError(value: string | null) {
    error.value = value
  }

  async function login(email: string, password: string) {
    loading.value = true
    error.value = null

    try {
      const firebaseUser = await loginWithEmail(email, password)
      user.value = firebaseUser
      await loadUserDocument()
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Error al iniciar sesión'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loginWithGoogle(options: GoogleLoginOptions = {}) {
    loading.value = true
    error.value = null
    isRegistering.value = true // Activar flag para evitar race condition

    try {
      console.log('🔐 Iniciando sesión con Google...')

      // 1. Autenticar con Google
      const firebaseUser = await authLoginWithGoogle()
      user.value = firebaseUser
      console.log(`✅ Usuario autenticado: ${firebaseUser.uid}`)

      // 2. Sólo desde el registro (casilla marcada): si la cuenta es nueva, se
      //    aprovisiona y se registra la aceptación. Desde el login NO se
      //    aprovisiona nada: lo hace el modal «Un último paso» (brief §5.2).
      //    La existencia se lee del SERVIDOR (ADR-021): decide una escritura.
      if (options.provisionIfNew) {
        const state = await fetchUserLegalState(firebaseUser.uid)
        if (!state.exists) {
          // Una sola derivación del nombre para `users` y el espejo; Auth después,
          // sólo si Google no dio nombre (ADR-023).
          await provisionProfessorAccount(firebaseUser)
          refreshUser()
          await useLegalConsentStore().acceptDuringSignup(firebaseUser.uid)
        }
      }

      // 3. Cargar documento de usuario (puede no existir aún: cuenta nueva desde el login)
      await loadUserDocument(true)

      console.log('✅ Login con Google completado exitosamente')
    } catch (err: unknown) {
      console.error('❌ Error al iniciar sesión con Google:', err)
      error.value = err instanceof Error ? err.message : 'Error al iniciar sesión con Google'
      throw err
    } finally {
      loading.value = false
      isRegistering.value = false // Desactivar flag
    }
  }

  /**
   * Termina una vinculación pendiente de Google (ADR-SEC-017): entra con la
   * contraseña de la cuenta existente —la prueba de posesión— y le vincula la
   * credencial de Google que `signInWithPopup` dejó sin usar.
   *
   * No consulta `emailVerified` en ningún punto: la política del owner es
   * vincular aunque el correo no esté verificado. Si la vinculación falla
   * DESPUÉS de entrar con contraseña, se cierra la sesión: la pantalla muestra
   * el error y no debe quedar una sesión a medias que el profesor no pidió.
   *
   * Los documentos ya existen (es la misma cuenta); `ensure*` es idempotente y
   * no los pisa — solo cubre una cuenta de Auth sin documentos.
   */
  async function linkGoogleToPasswordAccount(
    email: string,
    password: string,
    credential: OAuthCredential
  ) {
    loading.value = true
    error.value = null
    isRegistering.value = true // mismo flag que el ingreso con Google: evita la carga duplicada del documento

    try {
      const passwordUser = await loginWithEmail(email, password)
      user.value = passwordUser

      let linkedUser: User
      try {
        linkedUser = await linkGoogleCredential(passwordUser, credential)
      } catch (linkErr) {
        await firebaseLogout()
        user.value = null
        userDocument.value = null
        throw linkErr
      }
      user.value = linkedUser

      await provisionProfessorAccount(linkedUser)
      refreshUser()
      await loadUserDocument(false)
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Error al vincular la cuenta de Google'
      throw err
    } finally {
      loading.value = false
      isRegistering.value = false
    }
  }

  async function register(email: string, password: string, displayName: string) {
    loading.value = true
    error.value = null
    isRegistering.value = true // Activar flag de registro

    try {
      console.log('🔐 Iniciando proceso de registro...')

      // 0. Una sola derivación del nombre (ADR-023): la MISMA cadena va a Firebase
      //    Auth, a `users.display_name` y al espejo `professor_profiles.display_name`.
      //    La validación del formulario guía al usuario; esto es la red de seguridad.
      const name = deriveDisplayName(displayName, email)

      // 1. Registrar en Firebase Auth
      console.log('1️⃣ Registrando en Firebase Auth...')
      const firebaseUser = await authRegisterWithEmail(email, password, name)
      user.value = firebaseUser // Establecer usuario pero NO cargar documento aún
      console.log(`✅ Usuario de Firebase Auth creado: ${firebaseUser.uid}`)

      // 2. Crear documento de usuario
      console.log('2️⃣ Creando documento de usuario en Firestore...')
      await userService.createUserDocument(firebaseUser, 'professor', name)

      // 2b. Crear el perfil de profesor — PENDIENTE DE ACTIVACIÓN (§7.1). Es lo que
      // hace profesor a la cuenta (ADR-SEC-020); la regla ya no mira `users.role`,
      // pero exige el correo del token, que el registro ya tiene.
      console.log('2️⃣b Creando perfil de profesor (pendiente de activación)...')
      await userService.ensureProfessorProfile(firebaseUser, name)

      // 2c. Aceptación de los términos (casilla del registro, `signup_checkbox`).
      // No bloquea el registro si falla: el guard la pedirá (brief §5.1).
      await useLegalConsentStore().acceptDuringSignup(firebaseUser.uid)

      // 3. Enviar email de verificación
      console.log('3️⃣ Enviando email de verificación...')
      await sendEmailVerification(firebaseUser)

      // 4. Cargar documento de usuario
      console.log('4️⃣ Cargando documento de usuario...')
      await loadUserDocument(false) // No silent, queremos ver si hay problemas

      console.log('✅ Proceso de registro completado exitosamente')
    } catch (err: unknown) {
      console.error('❌ Error en el proceso de registro:', err)
      error.value = err instanceof Error ? err.message : 'Error al registrarse'
      throw err
    } finally {
      loading.value = false
      isRegistering.value = false // Desactivar flag de registro
    }
  }

  async function logout() {
    loading.value = true
    error.value = null

    try {
      await firebaseLogout()
      user.value = null
      userDocument.value = null
      useLegalConsentStore().reset()
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Error al cerrar sesión'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    userDocument,
    loading,
    error,
    isAuthenticated,
    userId,
    userEmail,
    displayName,
    setUser,
    refreshUser,
    setLoading,
    setError,
    login,
    loginWithGoogle,
    linkGoogleToPasswordAccount,
    register,
    logout,
    loadUserDocument,
  }
})
